import React from "react";
import { motion } from "framer-motion";
import { Cpu, Network, Users } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import RotatingCoin from "@/components/RotatingCoin";

const About = () => {
  const { t } = useI18n();

  const pillars = [
    { key: "digital", icon: Cpu, data: t.about.pillars.digital },
    { key: "decentralized", icon: Network, data: t.about.pillars.decentralized },
    { key: "peer", icon: Users, data: t.about.pillars.peer },
  ];

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left: title and body */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <div className="inline-flex items-center gap-3 text-xs tracking-[0.28em] uppercase text-[var(--gold-bright)]">
              <span className="h-px w-8 bg-[rgba(212,175,55,0.5)]" />
              <span>{t.about.kicker}</span>
            </div>
            <h2
              data-testid="about-title"
              className="font-heading mt-5 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[var(--text-main)]"
            >
              {t.about.title}
            </h2>
            <p
              data-testid="about-body"
              className="mt-6 text-base sm:text-lg leading-relaxed text-[var(--text-muted)] max-w-2xl"
            >
              {t.about.body}
            </p>
          </motion.div>

          {/* Right: floating coin */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-5 flex justify-center lg:justify-end"
            data-testid="about-coin-wrap"
          >
            <RotatingCoin variant="static" size={180} testId="about-coin" />
          </motion.div>
        </div>

        {/* Pillars bento grid */}
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.key}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`about-pillar-${p.key}`}
                className="group relative overflow-hidden rounded-2xl glow-border glow-border-hover p-6 sm:p-7 bg-[var(--bg-elevated)] transition-all"
              >
                <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{
                    background:
                      "radial-gradient(circle, rgba(212,175,55,0.15), transparent 60%)",
                  }}
                />
                <div className="relative">
                  <div className="inline-flex items-center justify-center h-11 w-11 rounded-xl border border-[rgba(212,175,55,0.25)] bg-[rgba(212,175,55,0.06)] text-[var(--gold-bright)]">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-heading mt-5 text-xl text-[var(--text-main)]">
                    {p.data.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[var(--text-muted)]">
                    {p.data.text}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;
